// Apply the store theme (Admin → Customize → Colours & Fonts) to the whole site by
// overriding the CSS variables from styles/variables.css on <html>.

const toRgb = (hex) => {
  const n = parseInt(String(hex).replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const toHex = (rgb) => `#${rgb.map(v => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('')}`;

// Blend colour a toward colour b (amount 0 → a, 1 → b)
const mix = (a, b, amount) => {
  const [x, y] = [toRgb(a), toRgb(b)];
  return toHex(x.map((v, i) => v + (y[i] - v) * amount));
};

const FONT_FALLBACKS = {
  serif: "Georgia, 'Times New Roman', serif",
  sans: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
};
const SERIF_FONTS = ['Playfair Display', 'Cormorant Garamond', 'Lora', 'Marcellus', 'Cinzel', 'Libre Baskerville', 'Prata'];
const fontStack = (name) => `'${name}', ${SERIF_FONTS.includes(name) ? FONT_FALLBACKS.serif : FONT_FALLBACKS.sans}`;

function loadFonts(fonts) {
  const families = [...new Set(fonts.filter(Boolean))].filter(f => f !== 'Inter'); // Inter is already in index.html
  const id = 'store-theme-fonts';
  let link = document.getElementById(id);
  if (!families.length) {
    link?.remove();
    return;
  }
  const href = `https://fonts.googleapis.com/css2?${families
    .map(f => `family=${encodeURIComponent(f).replace(/%20/g, '+')}:wght@400;500;600;700;800`)
    .join('&')}&display=swap`;
  if (!link) {
    link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
}

export function applyTheme(theme) {
  if (!theme) return;
  const root = document.documentElement.style;
  const { primaryColor, highlightColor, textColor, mutedTextColor, bgColor, surfaceColor, saleColor } = theme;

  const vars = {
    '--color-primary': primaryColor,
    '--color-primary-hover': mix(primaryColor, '#000000', 0.22),
    '--color-primary-light': mix(primaryColor, '#FFFFFF', 0.94),
    '--color-primary-subtle': mix(primaryColor, '#FFFFFF', 0.9),
    '--color-primary-border': mix(primaryColor, '#FFFFFF', 0.8),
    '--color-highlight': highlightColor,
    '--color-text': textColor,
    '--color-text-muted': mutedTextColor,
    '--color-text-light': mix(mutedTextColor, bgColor, 0.35),
    '--color-bg': bgColor,
    '--color-surface': surfaceColor,
    '--color-border': mix(surfaceColor, textColor, 0.1),
    '--color-border-light': mix(surfaceColor, textColor, 0.05),
    '--color-sale': saleColor,
    '--font-serif': fontStack(theme.headingFont),
    '--font-sans': fontStack(theme.bodyFont)
  };

  Object.entries(vars).forEach(([name, value]) => {
    if (value && !String(value).includes('NaN')) root.setProperty(name, value);
  });
  // The page background itself
  document.body.style.backgroundColor = bgColor;

  loadFonts([theme.headingFont, theme.bodyFont]);
}
